import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function UserManagement() {
  const { user } = useAuth();
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: ''
  });

  // Verificar que el usuario sea admin
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-6">
          <p className="text-slate-600">Solo los administradores pueden acceder a esta página.</p>
        </Card>
      </div>
    );
  }

  useEffect(() => {
    fetchAssistants();
  }, []);

  const fetchAssistants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/assistants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssistants(response.data);
    } catch (error) {
      console.error('Error fetching assistants:', error);
      toast.error('Error al cargar los asistentes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos obligatorios
    if (!formData.email || !formData.name) {
      toast.error('Email y nombre son obligatorios');
      return;
    }

    if (!editingAssistant && !formData.password) {
      toast.error('La contraseña es obligatoria para nuevos usuarios');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingAssistant) {
        // Actualizar asistente
        const updateData = {
          name: formData.name,
          ...(formData.password && { password: formData.password })
        };
        
        await axios.put(
          `${API_URL}/api/users/assistants/${editingAssistant.id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Asistente actualizado correctamente');
      } else {
        // Crear nuevo asistente
        await axios.post(
          `${API_URL}/api/users/assistants`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Asistente creado correctamente');
      }

      setDialogOpen(false);
      setFormData({ email: '', name: '', password: '' });
      setEditingAssistant(null);
      fetchAssistants();
    } catch (error) {
      console.error('Error saving assistant:', error);
      const message = error.response?.data?.detail || 'Error al guardar el asistente';
      toast.error(message);
    }
  };

  const handleDelete = async (assistantId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este asistente?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/users/assistants/${assistantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asistente eliminado correctamente');
      fetchAssistants();
    } catch (error) {
      console.error('Error deleting assistant:', error);
      toast.error('Error al eliminar el asistente');
    }
  };

  const openEditDialog = (assistant) => {
    setEditingAssistant(assistant);
    setFormData({
      email: assistant.email,
      name: assistant.name,
      password: '' // No mostrar contraseña existente
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingAssistant(null);
    setFormData({ email: '', name: '', password: '' });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-8 h-8" />
              Gestión de Usuarios
            </h1>
            <p className="text-slate-600 mt-1">Administra los usuarios asistentes del sistema</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Nuevo Asistente
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingAssistant ? 'Editar Asistente' : 'Nuevo Asistente'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!!editingAssistant}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">
                    Contraseña {!editingAssistant && '*'}
                    {editingAssistant && ' (dejar en blanco para mantener la actual)'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingAssistant}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      setFormData({ email: '', name: '', password: '' });
                      setEditingAssistant(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit">
                    {editingAssistant ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Lista de asistentes */}
        {assistants.length === 0 ? (
          <Card className="p-8 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">No hay asistentes registrados</p>
            <Button onClick={openCreateDialog}>
              Crear primer asistente
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {assistants.map((assistant) => (
              <Card key={assistant.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{assistant.name}</h3>
                    <p className="text-sm text-slate-600">{assistant.email}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Creado: {new Date(assistant.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(assistant)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(assistant.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
